// A customer's VPS on Loot → Products: the server facts and live checks.
//
// Facts render at first paint from the product row's `server` block; the
// live view (droplet read + DNS/HTTPS) fills in after. Attaching the
// customer's domain is the row's "Go live" button (HostingGoLive), one door
// for shared and VPS placeholders alike. No credentials here.

import { useCallback, useEffect, useState } from 'react';
import { getHostingServer } from '../api';
import { vpsFacts, domainCheckLine, attachState, TONE_COLOR } from '../lib/vpsServer';

const btn = { padding: '4px 10px', background: 'transparent', color: 'var(--text2)', border: '1px solid var(--border)', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--mono)' };
const label = { fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text3)', fontFamily: 'var(--mono)' };

export default function VpsServerPanel({ domain, summary }) {
  const [live, setLive] = useState(null);
  const [liveBusy, setLiveBusy] = useState(false);

  const load = useCallback(async () => {
    setLiveBusy(true);
    try { setLive(await getHostingServer(domain)); }
    catch (e) { setLive({ error: e.message }); }
    setLiveBusy(false);
  }, [domain]);
  useEffect(() => { load(); }, [load]);

  const facts = vpsFacts(summary, live);
  const checks = (live && !live.error ? live.domainChecks || [] : []).map(domainCheckLine).filter(Boolean);
  const attach = attachState(summary);

  return (
    <div style={{ padding: '10px 0 12px', fontFamily: 'var(--mono)', fontSize: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={label}>Server</span>
        <button onClick={load} disabled={liveBusy} style={{ ...btn, opacity: liveBusy ? 0.6 : 1 }}>
          {liveBusy ? 'Checking…' : 'Re-check'}
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 14, rowGap: 3 }}>
        {facts.map(f => (
          <div key={f.label} style={{ display: 'contents' }}>
            <span style={{ color: 'var(--text3)' }}>{f.label}</span>
            <span style={{ color: TONE_COLOR[f.tone] || 'var(--text)', wordBreak: 'break-all' }}>
              {f.value}
              {f.copy && (
                <button
                  onClick={() => navigator.clipboard?.writeText(f.copy)}
                  title={`Copy ${f.label}`}
                  style={{ ...btn, padding: '0 6px', marginLeft: 6, fontSize: 10 }}
                >copy</button>
              )}
            </span>
          </div>
        ))}
      </div>
      {live?.error && <div style={{ color: TONE_COLOR.bad, marginTop: 6 }}>⚠ Live check failed: {live.error}</div>}
      {checks.map(c => (
        <div key={c.text} style={{ color: TONE_COLOR[c.tone], marginTop: 6 }}>{c.tone === 'ok' ? '✓' : '⚠'} {c.text}</div>
      ))}

      {attach.offer && (
        <div style={{ color: TONE_COLOR.warn, marginTop: 8 }}>No domain yet. Attach the customer's with Go live on this row.</div>
      )}
      {!attach.offer && attach.reason && (
        <div style={{ color: TONE_COLOR.muted, marginTop: 8 }}>Go live unavailable: {attach.reason}</div>
      )}
    </div>
  );
}
